/**
 * Široki celi brojevi — tačno, bez `Number`.
 *
 * Čvor nam `uint64`, `int64` i `uint128` vraća kao STRING, i to namerno: JSON
 * nema 128-bitne cele brojeve, a `Number` gubi cifre već iznad 2^53. Do sada je
 * taj tačan string na povratku prolazio kroz `Number()`, pa je unos kroz GUI
 * kvario vrednost — tiho i za malo:
 *
 *     9007199254740993      -> 9007199254740992
 *     14579517313615008891  -> 14579517313615009792
 *
 * Ista disciplina koju drži i MongoDB-ov JS drajver: za `Long` i `Decimal128`
 * nikad ne vraća `Number`, nego omotač čiji je `toString()` tačan. Nama omotač
 * ne treba — dovoljno je da string ostane string, a `BigInt` služi samo za
 * poređenje i proveru opsega.
 */

const RANGES = {
    uint128_t: [0n, (1n << 128n) - 1n],
    // Za `Decimal128` iz Mongo-a: 34 cifre ne staju u `int64` (18), a
    // `int128` nosi 39. Vidi 5c.
    int128_t: [-(1n << 127n), (1n << 127n) - 1n],
    uint64_t: [0n, (1n << 64n) - 1n],
    int64_t: [-(1n << 63n), (1n << 63n) - 1n],
};

/** Tipovi koje `Number` ne nosi, pa se kroz naš sloj prenose kao string. */
export const isWideType = (type) => Object.prototype.hasOwnProperty.call(RANGES, type);

/**
 * Opsezi SVIH celobrojnih tipova — za proveru vrednosti, ne za prenos.
 *
 * Namerno ODVOJENO od `RANGES`. Ta tabela odgovara na pitanje „da li vrednost
 * mora da putuje kao niska", i po njoj radi `isWideType`. Da su uski tipovi
 * dodati u nju, `uint8_t` bi postao „širok" i put podataka bi se promenio — a
 * trebalo je samo proveriti opseg.
 *
 * Blizanac je `apps/backend/blockchain/utils/exactInteger.js`; ko menja jedan,
 * menja i drugi.
 */
const NARROW_RANGES = {
    uint8_t: [0n, 255n],
    int8_t: [-128n, 127n],
    uint16_t: [0n, 65535n],
    int16_t: [-32768n, 32767n],
    uint32_t: [0n, 4294967295n],
    int32_t: [-2147483648n, 2147483647n],
};

const INT_RANGES = { ...RANGES, ...NARROW_RANGES };

/**
 * Ceo broj iz korisničkog unosa, ili `null` ako to nije ceo broj.
 * Namerno ne prihvata `1e5` ni `1.0` — oba bi morala preko `Number`.
 */
export const parseIntegerString = (value) => {
    const s = String(value ?? '').trim();
    if (!/^[+-]?\d+$/.test(s)) return null;
    try {
        return BigInt(s);
    } catch {
        return null;
    }
};

/**
 * Da li unos staje u opseg tipa. Nepoznat tip se ne osporava.
 *
 * Do 28.08.2026. je gledao samo `RANGES`, dakle 64- i 128-bitne tipove, pa je
 * `300` za `uint8_t` prolazio kao ispravan. Odbijao ga je tek serijalizator pri
 * slanju, porukom bez imena kolone — umesto da polje to kaže dok se kuca.
 */
export const fitsType = (value, type) => {
    const range = INT_RANGES[type];
    if (!range) return true;
    const n = parseIntegerString(value);
    if (n === null) return false;
    return n >= range[0] && n <= range[1];
};

/**
 * Vrednost spremna za slanje: normalizovan decimalni string bez vodećih nula i
 * bez suvišnog `+`. Vraća `null` kad unos nije ceo broj, da pozivalac ne pošalje
 * na lanac nešto što tamo ne može da se pročita.
 */
export const toWireInteger = (value) => {
    const n = parseIntegerString(value);
    return n === null ? null : n.toString();
};
